import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Facts, PageHeader } from '../components/page';
import { ActionError, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/input';
import { useApi } from '../lib/auth';
import { instant } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import { useTenant } from '../lib/referentials';
import type { ImportBatch } from '../lib/types';
import { ImportStatusBadge } from './imports';

// Fiche d'un import (BA-013) : simulation puis import réel, progression, résumé et rapport.

const RUNNING = new Set(['validating', 'processing']);

export function ImportDetailPage() {
  const { t, language } = useI18n();
  const api = useApi();
  const { id } = useParams({ strict: false }) as { id: string };
  const tz = useTenant().data?.timezone;
  const [markAbsent, setMarkAbsent] = useState(false);
  const query = useApiQuery<ImportBatch>(`/v1/imports/${id}`, undefined, {
    refetchInterval: (q) => (q.state.data && RUNNING.has(q.state.data.status) ? 2000 : false),
  });
  const run = useApiMutation<{ dryRun: boolean; markAbsentAsCancelled?: boolean }, ImportBatch>(
    'POST',
    `/v1/imports/${id}/run`,
    { invalidate: [`/v1/imports`] },
  );

  async function downloadReport(batch: ImportBatch) {
    const res = await api.request<Response>('GET', `/v1/imports/${id}/report`, { raw: true });
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport-${batch.filename.replace(/\.[^.]+$/, '')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <QueryView query={query}>
      {(b) => {
        const running = RUNNING.has(b.status);
        const canRun = b.mapping !== null && !running;
        const percent = b.progress.total
          ? Math.round((b.progress.done / b.progress.total) * 100)
          : 0;
        return (
          <>
            <PageHeader
              title={b.filename}
              subtitle={
                <span className="flex gap-2">
                  <ImportStatusBadge batch={b} />
                  <Badge>{t.imports.kinds[b.kind]}</Badge>
                  <Badge>{b.dryRun ? t.imports.simulation : t.imports.real}</Badge>
                </span>
              }
            />
            <div className="flex flex-col gap-4">
              <Card>
                <CardContent className="flex flex-col gap-4">
                  {running ? (
                    <div>
                      <p className="mb-1 text-sm">
                        {t.imports.progress} : {percent} %
                      </p>
                      <div
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                        aria-label={t.imports.progress}
                        className="h-2 w-full overflow-hidden rounded bg-muted"
                      >
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  ) : null}
                  <Facts
                    items={[
                      [t.imports.rows, String(b.rowsTotal)],
                      [t.imports.rowsOk, String(b.rowsOk)],
                      [t.imports.rowsError, String(b.rowsError)],
                      [t.imports.rowsWarning, String(b.rowsWarning)],
                      [t.imports.createdAt, instant(b.createdAt, tz, language)],
                      [
                        t.imports.simulation,
                        b.dryRunCompletedAt ? instant(b.dryRunCompletedAt, tz, language) : null,
                      ],
                    ]}
                  />
                  {b.errorMessage ? (
                    <p role="alert" className="text-sm text-destructive">
                      {b.errorMessage}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
              {Object.keys(b.summary).length ? (
                <Card>
                  <CardHeader>
                    <CardTitle>{t.imports.summary}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Facts
                      items={Object.entries(b.summary).map(([key, value]) => [key, String(value)])}
                    />
                  </CardContent>
                </Card>
              ) : null}
              <Card>
                <CardContent className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="outline"
                    disabled={!canRun || run.isPending}
                    onClick={() => run.mutate({ dryRun: true })}
                  >
                    {t.imports.dryRun}
                  </Button>
                  {b.kind === 'contracts' ? (
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={markAbsent}
                        onChange={(e) => setMarkAbsent(e.target.checked)}
                      />
                      {t.imports.markAbsent}
                    </label>
                  ) : null}
                  <ConfirmButton
                    label={t.imports.run}
                    title={t.imports.run}
                    message={t.imports.runConfirm}
                    variant="default"
                    disabled={!canRun || !b.dryRunCompletedAt}
                    onConfirm={() =>
                      run.mutateAsync({
                        dryRun: false,
                        ...(b.kind === 'contracts' ? { markAbsentAsCancelled: markAbsent } : {}),
                      })
                    }
                  />
                  {b.hasReport ? (
                    <Button variant="ghost" onClick={() => void downloadReport(b)}>
                      {t.imports.report}
                    </Button>
                  ) : null}
                  {!b.dryRunCompletedAt ? (
                    <p className="w-full text-xs text-muted-foreground">
                      {t.imports.dryRunRequired}
                    </p>
                  ) : null}
                  <ActionError error={run.error} />
                </CardContent>
              </Card>
            </div>
          </>
        );
      }}
    </QueryView>
  );
}
